/**
 * Push Notification Service for ForbrugerAgenten Mobile
 * Uses Expo Notifications for cross-platform push (APNS + FCM)
 */
import { Platform } from 'react-native'
import * as Notifications from 'expo-notifications'
import * as Device from 'expo-device'
import Constants from 'expo-constants'
import { apiClient } from '../api/client'
import { storage } from '../utils/storage'

const PUSH_TOKEN_KEY = 'push_token'

// Configure notification behavior when app is in foreground
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
})

/**
 * Request notification permissions and register push token with backend.
 * Safe to call multiple times — deduplicates token registration.
 */
export async function registerForPushNotifications(userId: string): Promise<string | null> {
  // Push notifications only work on physical devices
  if (!Device.isDevice) {
    console.log('[Push] Not a physical device — skipping push registration')
    return null
  }

  // Web doesn't use Expo push tokens
  if (Platform.OS === 'web') {
    return null
  }

  const { status: existingStatus } = await Notifications.getPermissionsAsync()
  let finalStatus = existingStatus

  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync()
    finalStatus = status
  }

  if (finalStatus !== 'granted') {
    console.log('[Push] Permission not granted')
    return null
  }

  // Get Expo push token
  const projectId = Constants.expoConfig?.extra?.eas?.projectId
  const tokenData = await Notifications.getExpoPushTokenAsync(
    projectId ? { projectId } : undefined
  )
  const token = tokenData.data

  // Only register if token changed
  const previousToken = await storage.get(PUSH_TOKEN_KEY)
  if (previousToken === token) {
    return token
  }

  // Register with backend
  try {
    await apiClient.users.registerPushToken(userId, token)
    await storage.set(PUSH_TOKEN_KEY, token)
    console.log('[Push] Token registered:', token.slice(0, 30) + '...')
  } catch (err) {
    console.error('[Push] Failed to register token:', err)
  }

  // Android: configure notification channel
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('default', {
      name: 'ForbrugerAgenten',
      importance: Notifications.AndroidImportance.HIGH,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#2563eb',
    })
  }

  return token
}

/**
 * Clear push token locally and deregister from backend (on logout or disable)
 */
export async function clearPushToken(userId?: string): Promise<void> {
  await storage.remove(PUSH_TOKEN_KEY)
  if (userId) {
    try {
      await apiClient.users.deregisterPushToken(userId)
    } catch {
      // Best-effort — token will be cleaned up by DeviceNotRegistered on next send
    }
  }
}

/**
 * Add a notification received listener
 */
export function onNotificationReceived(
  callback: (notification: Notifications.Notification) => void
): Notifications.EventSubscription {
  return Notifications.addNotificationReceivedListener(callback)
}

/**
 * Add a notification response listener (user tapped notification)
 */
export function onNotificationTapped(
  callback: (response: Notifications.NotificationResponse) => void
): Notifications.EventSubscription {
  return Notifications.addNotificationResponseReceivedListener(callback)
}

/**
 * Get current badge count
 */
export async function getBadgeCount(): Promise<number> {
  if (Platform.OS === 'web') return 0
  return Notifications.getBadgeCountAsync()
}

/**
 * Set badge count
 */
export async function setBadgeCount(count: number): Promise<void> {
  if (Platform.OS === 'web') return
  await Notifications.setBadgeCountAsync(count)
}

/**
 * Dismiss all delivered notifications and reset OS badge to 0.
 * Called when the app comes to the foreground so the notification tray and
 * badge don't pile up state the user has already seen.
 * No-op on web (Expo notifications API is native-only).
 */
export async function clearAllNotifications(): Promise<void> {
  if (Platform.OS === 'web') return
  try {
    await Notifications.dismissAllNotificationsAsync()
    await Notifications.setBadgeCountAsync(0)
  } catch (e) {
    console.warn('[notifications] clearAllNotifications failed', e)
  }
}
